import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { ResourceCardLogo } from 'components/blocks/resource-card';
import { useGetAgentById } from '../hooks/use-agents';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useAgentSelectionStore } from 'state/store/chat/use-agent-selection-store';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { buildChatLandingPath } from 'utils/chat-utilities';
import type { PlatformResourceChangedData } from 'models/websockets.types';

interface TryAgentBadgeProps {
  change: PlatformResourceChangedData;
}

export const TryAgentBadge: React.FC<TryAgentBadgeProps> = ({ change }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = useCommonStore((state) => state.user?.id);
  const isIncognitoChat = searchParams.get('incognito-chat') === 'true';
  // Old messages re-render this card often; the logo rarely changes, so don't refetch on mount.
  const { data: agentData } = useGetAgentById(
    { id: change.resourceId },
    { staleTime: Infinity, refetchOnMount: false }
  );

  const handleClick = useCallback(() => {
    if (!change.resourceId) return;
    if (userId) {
      useAgentSelectionStore.getState().setSelectedAgentId(userId, change.resourceId);
    }
    useChatStore.getState().setActiveConversationId(null);
    useChatStore.getState().setReplyMessage(null);
    navigate(
      buildChatLandingPath({
        agentId: change.resourceId,
        isIncognito: isIncognitoChat,
      })
    );
  }, [change.resourceId, userId, isIncognitoChat, navigate]);

  if (!change.resourceId) return null;

  const agentName = change.name || agentData?.agent?.name || t('THIS_AGENT');
  const landingPath = buildChatLandingPath({
    agentId: change.resourceId,
    isIncognito: isIncognitoChat,
  });

  return (
    <div className="flex w-full items-center gap-3 rounded-xl border border-grey-100 bg-white p-4 shadow-choice-card dark:border-border dark:bg-card sm:gap-4 sm:p-5">
      <ResourceCardLogo
        src={agentData?.agent?.logo_url}
        name={agentName}
        className="size-7 rounded-full p-0"
      >
        <Bot className="size-4" />
      </ResourceCardLogo>
      <div className="flex min-w-0 flex-1 flex-col text-sm text-grey-900 dark:text-foreground">
        <p className="break-words">{t('AGENT_READY_TO_USE', { name: agentName })}</p>
        <p className="break-words">
          {t('START_CHATTING_WITH')} <span className="font-semibold">{agentName}</span>
        </p>
      </div>
      <a
        href={landingPath}
        onClick={(e) => {
          e.preventDefault();
          handleClick();
        }}
        className="shrink-0 rounded-full bg-grey-950 px-4 py-1.5 text-xs font-medium text-true-white no-underline transition-opacity hover:opacity-80 active:scale-95 dark:bg-true-white dark:text-grey-950"
      >
        {t('START_CHAT')}
      </a>
    </div>
  );
};

interface TryAgentBadgesListProps {
  changes?: PlatformResourceChangedData[];
}

export const TryAgentBadgesList: React.FC<TryAgentBadgesListProps> = ({ changes }) => {
  if (!changes || changes.length === 0) return null;

  const createdAgents = changes.filter(
    (res, idx, arr) =>
      (res.kind === 'agents' || !res.kind) &&
      res.action !== 'deleted' &&
      Boolean(res.resourceId) &&
      arr.findIndex((r) => r.resourceId === res.resourceId) === idx
  );

  if (createdAgents.length === 0) return null;

  return (
    <div className="mt-3 flex flex-col gap-2">
      {createdAgents.map((res) => (
        <TryAgentBadge key={res.resourceId} change={res} />
      ))}
    </div>
  );
};
